<template>
  <div class="flex w-full flex-col gap-[15px]">
    <div
      v-for="product in store.bumProductList"
      :key="`bump-product-${product.priceId}-${product.productId}`"
      class="flex flex-col items-start self-stretch rounded-lg border-dashed border-gray-200"
      :style="{
        borderColor: '#' + store.payment.bumpSettings.bumpBorderColor,
      }"
    >
      <div
        class="border-bottom flex items-center justify-between gap-2 self-stretch rounded-t-lg border-gray-200 px-4 py-[10px]"
      >
        <div class="flex items-center gap-2 self-stretch">
          <UITextSmMedium
            :style="{
              color: '#' + store.payment.bumpSettings.highlightTemplateThreeColor,
            }"
            >{{ store.payment.bumpSettings.highlightText }}</UITextSmMedium
          >
          <img
            :src="
              BUMP_ICONS.find((icon) => icon.value === store.payment.bumpSettings.bumpIcon)
                ?.imageUrl
            "
            :alt="t('common.products.bumpIconAlt')"
            class="h-6 w-6"
            loading="lazy"
          />
        </div>
        <div class="flex items-center justify-center gap-2 self-stretch">
          <label class="switch">
            <input type="checkbox" checked />
            <span class="slider round"></span>
          </label>
        </div>
      </div>
      <div class="flex w-full items-start gap-2 self-stretch p-4">
        <div class="flex w-full items-start gap-2 self-stretch">
          <img
            v-if="
              product.enableImage &&
              (product.uploadedImage?.length > 0 || product.image?.length > 0)
            "
            :key="`product-image-${product.productId}-${product.uploadedImage || product.image}`"
            :src="product.uploadedImage || product.image"
            :alt="t('common.products.productImageAlt')"
            class="h-20 w-20 rounded-lg"
            loading="lazy"
            @error="handleImageError"
          />
          <div v-else-if="product.enableImage" class="h-20 w-20 rounded-lg bg-[lightgray]"></div>

          <div class="flex w-full flex-col items-start gap-2 self-stretch">
            <div class="flex w-full items-start justify-between gap-2 self-stretch">
              <UITextSmMedium
                :style="{
                  color: '#' + store.payment.bumpSettings.headlineColor,
                }"
                >{{ product.headline }}</UITextSmMedium
              >
              <div
                v-if="store.payment.bumpSettings.showPrice"
                class="flex items-start gap-[5px] self-stretch"
                :style="{
                  color: '#' + store.payment.bumpSettings.bumpPriceColor,
                }"
              >
                <UITextSmMedium
                  v-if="product.compareAtPrice"
                  class="overflow-hidden text-ellipsis line-through"
                  >{{ store.currencyList[product.currency]?.symbol || "$"
                  }}{{ product.compareAtPrice }}
                </UITextSmMedium>
                <UITextSmMedium
                  >{{ store.currencyList[product.currency]?.symbol || "$"
                  }}{{ product.amount }}</UITextSmMedium
                >
              </div>
            </div>

            <UITextSmMedium
              :style="{
                color: '#' + store.payment.bumpSettings.otoHeadlineColor,
              }"
              >{{ product.otoHeadline }}</UITextSmMedium
            >
            <UITextSmMedium
              class="whitespace-pre-wrap"
              :style="{
                color: '#' + store.payment.bumpSettings.otoTextColor,
              }"
              >{{ product.otoText }}</UITextSmMedium
            >
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { UITextSmMedium } from "@gohighlevel/ghl-ui";

import { useAppStore } from "@/store/app";
import { BUMP_ICONS } from "@/util/productConstants";
import { useI18n } from "vue-i18n";

const store = useAppStore();
const { t } = useI18n();

const handleImageError = (event: Event) => {
  const img = event.target as HTMLImageElement;
  // Set a fallback image or hide the image element
  img.style.display = "none";
};
</script>

<style scoped>
.border-bottom {
  border-bottom: 2px solid #eaecf0;
  border-style: solid;
  border-top: none;
  border-left: none;
  border-right: none;
}

.switch {
  position: relative;
  display: inline-block;
  width: 40px;
  height: 22px;
  margin-bottom: 0 !important;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #d0d5dd;
  transition: 0.2s ease-in-out;
  border-radius: 10px;
}

.slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 0px;
  bottom: 2px;
  background-color: white;
  transition: 0.2s ease-in-out;
  border-radius: 50%;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
}

input:checked + .slider {
  background-color: #2970ff;
}

input:focus + .slider {
  box-shadow: 0 0 0 3px rgba(41, 112, 255, 0.1);
}

input:checked + .slider:before {
  transform: translateX(20px);
}

.slider.round {
  border-radius: 10px;
}

.slider.round:before {
  border-radius: 50%;
}
</style>
